import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtIDR, fmtNum, FUEL_META, todayISO } from "@/lib/format";
import { Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { ExcelImportButtons } from "@/components/ExcelImportButtons";

const Pembelian = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [fuels, setFuels] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ fuel_code: "pertalite", liters: "", unit_cost: "", date: todayISO(), do_number: "", supplier: "PT Pertamina" });

  const load = () => api.get("/purchases").then(r => setRows(r.data));
  useEffect(() => { load(); api.get("/fuels").then(r => setFuels(r.data)); }, []);

  useEffect(() => {
    const f = fuels.find(x => x.code === form.fuel_code);
    if (f && !form.unit_cost) setForm(prev => ({ ...prev, unit_cost: f.buy_price }));
    // eslint-disable-next-line
  }, [form.fuel_code, fuels]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/purchases", { ...form, liters: parseFloat(form.liters), unit_cost: parseFloat(form.unit_cost) });
      toast.success("Pembelian tersimpan");
      setOpen(false); load();
      setForm({ fuel_code: "pertalite", liters: "", unit_cost: "", date: todayISO(), do_number: "", supplier: "PT Pertamina" });
    } catch (e) { toast.error("Gagal menyimpan"); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus penerimaan ini?")) return;
    await api.delete(`/purchases/${id}`); toast.success("Terhapus"); load();
  };

  const estTotal = parseFloat(form.liters || 0) * parseFloat(form.unit_cost || 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Pembelian / Penerimaan Stock</h1>
          <p className="text-sm text-slate-500 mt-1">Delivery Order dari Pertamina</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExcelImportButtons endpoint="/import/purchases.xlsx" templateEndpoint="/import/purchases-template.xlsx" filename="purchases_template.xlsx" onDone={load} testidPrefix="purchases-import" />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-purchase-button" className="bg-[#005DAA] hover:bg-[#004885]"><Plus className="w-4 h-4 mr-2" />Terima Stock</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Penerimaan Stock BBM</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label>Jenis BBM</Label>
                <Select value={form.fuel_code} onValueChange={(v) => setForm(f => ({ ...f, fuel_code: v, unit_cost: "" }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {fuels.map(f => <SelectItem key={f.code} value={f.code}>{f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Liter</Label>
                  <Input type="number" step="0.01" value={form.liters} onChange={(e) => setForm(f => ({ ...f, liters: e.target.value }))} required data-testid="purchase-liters-input" />
                </div>
                <div className="space-y-2"><Label>Harga Beli / L</Label>
                  <Input type="number" step="0.01" value={form.unit_cost} onChange={(e) => setForm(f => ({ ...f, unit_cost: e.target.value }))} required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Tanggal</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} required />
                </div>
                <div className="space-y-2"><Label>No. DO</Label>
                  <Input value={form.do_number} onChange={(e) => setForm(f => ({ ...f, do_number: e.target.value }))} placeholder="DO-2026-0001" />
                </div>
              </div>
              <div className="space-y-2"><Label>Supplier</Label>
                <Input value={form.supplier} onChange={(e) => setForm(f => ({ ...f, supplier: e.target.value }))} />
              </div>
              <div className="rounded-lg bg-slate-50 p-3 flex items-center justify-between">
                <span className="text-sm text-slate-600">Total Pembelian</span>
                <span className="text-xl font-extrabold font-mono-fig text-[#005DAA]">{fmtIDR(estTotal)}</span>
              </div>
            <DialogFooter><Button type="submit" className="bg-[#005DAA] hover:bg-[#004885]">Simpan</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg font-bold">Riwayat Penerimaan</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tanggal</TableHead>
                <TableHead>BBM</TableHead>
                <TableHead className="text-right">Liter</TableHead>
                <TableHead className="text-right">Harga/L</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>No. DO</TableHead>
                <TableHead>Supplier</TableHead>
                {user?.role === "admin" && <TableHead></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(r => {
                const m = FUEL_META[r.fuel_code];
                return (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono-fig text-sm">{r.date}</TableCell>
                    <TableCell><Badge className={`${m.bg} ${m.text} border ${m.border}`}>{m.name}</Badge></TableCell>
                    <TableCell className="text-right font-mono-fig">{fmtNum(r.liters)}</TableCell>
                    <TableCell className="text-right font-mono-fig">{fmtIDR(r.unit_cost)}</TableCell>
                    <TableCell className="text-right font-mono-fig font-bold">{fmtIDR(r.total)}</TableCell>
                    <TableCell className="font-mono-fig text-sm">{r.do_number || "-"}</TableCell>
                    <TableCell className="text-sm">{r.supplier}</TableCell>
                    {user?.role === "admin" && (
                      <TableCell><Button size="icon" variant="ghost" onClick={() => del(r.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button></TableCell>
                    )}
                  </TableRow>
                );
              })}
              {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">Belum ada penerimaan</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Pembelian;
